import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import jwt_decode from 'jwt-decode';

import {
  InputRefreshTokenDto,
  InputSignInUserDto,
  OutputAllMenuPagesPermissionDto,
  UserManagementService,
} from '../../shared/api';

export const TOKEN_NAME = 'token';
export const REFRESH_TOKEN_NAME = 'refreshToken';

export type LoginResult = 'success' | 'invalid' | 'disabled';

/**
 * Modern authentication service using inject() pattern
 * Handles JWT token management, login, and permissions
 */
@Injectable({ providedIn: 'root' })
export class AuthService {
  private userManagementService = inject(UserManagementService);
  private router = inject(Router);

  currentPage = 0;

  isLogin(): boolean {
    const token = this.getToken();
    if (token !== null && !this.isTokenExpired(token)) {
      this.setMenuPagePermission();
      return true;
    }
    return false;
  }

  getToken(): string | null {
    return localStorage.getItem(TOKEN_NAME);
  }

  setToken(token: string): void {
    localStorage.setItem(TOKEN_NAME, token);
  }

  getRefreshToken(): string | null {
    return localStorage.getItem(REFRESH_TOKEN_NAME);
  }

  setRefreshToken(refreshToken: string): void {
    localStorage.setItem(REFRESH_TOKEN_NAME, refreshToken);
  }

  getTokenExpirationDate(token: string): Date | null {
    const decoded = jwt_decode(token) as any;

    if (decoded.exp === undefined) {
      return null;
    }

    const date = new Date(0);
    date.setUTCSeconds(decoded.exp);
    return date;
  }

  getRoleId(): string | null {
    const token = this.getToken();
    if (!token) return null;

    const decoded = jwt_decode(token) as any;
    return decoded.RoleId ?? null;
  }

  getGivenName(): string | null {
    const token = this.getToken();
    if (!token) return null;

    const decoded = jwt_decode(token) as any;
    return decoded.given_name ?? null;
  }

  getEmail(): string | null {
    const token = this.getToken();
    if (!token) return null;

    const decoded = jwt_decode(token) as any;
    return decoded.email ?? decoded['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress'] ?? null;
  }

  isTokenExpired(token?: string): boolean {
    if (!token) {
      token = this.getToken();
    }
    if (!token) {
      return true;
    }

    const date = this.getTokenExpirationDate(token);
    if (date === null) {
      return false;
    }
    return !(date.valueOf() > new Date().valueOf());
  }

  async login(inputSignInUserDto: InputSignInUserDto): Promise<LoginResult> {
    try {
      const result = await this.userManagementService
        .userManagementSignInPost({ body: inputSignInUserDto })
        .toPromise();

      // The API returns HTTP 200 with a null body when credentials are wrong.
      if (!result || !result.token) {
        return 'invalid';
      }

      this.setToken(result.token);
      this.setRefreshToken(result.refreshToken);
      this.refreshToken(result.token, result.refreshToken);

      return 'success';
    } catch (error: any) {
      // 403 → account disabled (server says so explicitly).
      if (error?.status === 403) {
        return 'disabled';
      }
      return 'invalid';
    }
  }

  async setMenuPagePermission(): Promise<void> {
    let env = localStorage.getItem('env');
    if (!env) {
      env = 'dev';
      localStorage.setItem('env', 'dev');
    }

    try {
      const permissions = await this.userManagementService
        .userManagementGetMenusPagesPermissionByRoleIdGet({
          roleId: +this.getRoleId(),
          env: env,
        })
        .toPromise();

      localStorage.setItem('Permission', JSON.stringify(permissions));
    } catch (error) {
      console.error('Failed to get permissions:', error);
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      this.router.navigate(['/Login']);
    }
  }

  async getMenuPagePermission(): Promise<OutputAllMenuPagesPermissionDto[]> {
    await this.setMenuPagePermission();
    const permissionsStr = localStorage.getItem('Permission');
    return permissionsStr ? JSON.parse(permissionsStr) : [];
  }

  hasPermission(controller: string, action: string): boolean {
    const permissionsStr = localStorage.getItem('Permission');
    if (!permissionsStr) return false;

    const menus: OutputAllMenuPagesPermissionDto[] = JSON.parse(permissionsStr);
    const controllerLower = controller.toLowerCase();
    const actionLower = action.toLowerCase();

    return menus.some((menu) =>
      menu.pages?.some((page) =>
        page.permissions?.some(
          (p) =>
            p.controller?.toLowerCase() === controllerLower &&
            p.action?.toLowerCase() === actionLower,
        ),
      ),
    );
  }

  refreshToken(token: string, refreshToken: string): void {
    const millisTill10 = new Date(this.getTokenExpirationDate(token)).getTime() - new Date().getTime();

    const timeout = setTimeout(() => {
      this.userManagementService
        .userManagementTokenPost({
          body: {
            token,
            refreshToken,
          } as InputRefreshTokenDto,
        })
        .subscribe({
          next: (value) => {
            this.setToken(value.token);
            this.setRefreshToken(value.refreshToken);
            this.refreshToken(value.token, value.refreshToken);
          },
          error: () => {
            clearTimeout(timeout);
          },
        });
    }, millisTill10);
  }

  async refreshCurrentToken(): Promise<boolean> {
    const token = this.getToken();
    const refreshToken = this.getRefreshToken();

    if (!token) {
      return false;
    }

    try {
      const result = await this.userManagementService
        .userManagementTokenPost({
          body: {
            token,
            refreshToken,
          } as InputRefreshTokenDto,
        })
        .toPromise();

      this.setToken(result.token);
      this.setRefreshToken(result.refreshToken);
      this.refreshToken(result.token, result.refreshToken);

      return true;
    } catch (error) {
      return false;
    }
  }
}
